import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { api, errMsg, fieldErrors } from "@/lib/api";
import { useStore, getUtm } from "@/context/StoreContext";
import { brl, digits, maskCep, maskCpf, maskPhone, parseSacola, validCpf, viaCep, track, useSeo } from "@/lib/fmt";
import { CartLines, FreeShipBar } from "@/components/store/CartDrawer";

const F = ({ id, label, err, children }) => (
  <div><label htmlFor={id} className="label-na">{label}</label>{children}{err && <p className="err-na" data-testid={`error-${id}`}>{err}</p>}</div>
);

export default function Checkout() {
  const { items, setItems, sessao } = useStore();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [ready, setReady] = useState(false);
  const [f, setF] = useState({ email: "", nome: "", cpf: "", celular: "", cep: "", rua: "", numero: "", complemento: "", bairro: "", cidade: "", uf: "" });
  const [metodo, setMetodo] = useState("pix");
  const [cupom, setCupom] = useState("");
  const [cupomOn, setCupomOn] = useState(null);
  const [frete, setFrete] = useState(null);
  const [ciencia, setCiencia] = useState(false);
  const [termos, setTermos] = useState(false);
  const [pr, setPr] = useState(null);
  const [errs, setErrs] = useState({});
  const [busy, setBusy] = useState(false);
  const loaded = useRef(false);
  useSeo("Checkout");

  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    const s = sp.get("sacola");
    if (s) {
      const parsed = parseSacola(s);
      if (parsed.length) { setItems(parsed); setReady(true); }
    }
    track("begin_checkout");
  }, [sp, setItems]);

  useEffect(() => {
    if (!items.length) return setPr(null);
    const t = setTimeout(() => api.post("/cart/price", { itens: items, cupom: cupomOn, cep: digits(f.cep).length === 8 ? digits(f.cep) : null, metodo, email: f.email || null, frete_servico: frete })
      .then((r) => setPr(r.data)), 200);
    return () => clearTimeout(t);
  }, [items, cupomOn, f.cep, metodo, frete, f.email]);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const onCep = async (e) => {
    const v = maskCep(e.target.value);
    setF((x) => ({ ...x, cep: v }));
    const a = await viaCep(v);
    if (a) setF((x) => ({ ...x, ...a }));
  };
  const saveCart = () => f.email && api.post("/cart/save", { sessao, email: f.email, nome: f.nome, itens: items, utm: getUtm() }).catch(() => {});

  const validate = () => {
    const e = {};
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email)) e.email = "E-mail inválido";
    if (f.nome.trim().split(/\s+/).length < 2) e.nome = "Informe nome e sobrenome";
    if (!validCpf(f.cpf)) e.cpf = "CPF inválido";
    if (digits(f.celular).length < 10) e.celular = "Celular com DDD";
    if (digits(f.cep).length !== 8) e.cep = "CEP inválido";
    if (!f.rua.trim()) e.rua = "Informe o endereço";
    if (!f.numero.trim()) e.numero = "Informe o número";
    if (pr?.restrito && !ciencia) e.ciencia_advertencias = "Confirme que leu as advertências";
    if (!termos) e.aceite_termos = "Aceite os termos";
    setErrs(e);
    return !Object.keys(e).length;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return toast.error("Confira os campos destacados");
    setBusy(true);
    try {
      const landing = sessionStorage.getItem("na_landing");
      const r = await api.post("/orders", {
        itens: items, cupom: cupomOn, cliente: { nome: f.nome, email: f.email, cpf: f.cpf, celular: f.celular },
        endereco: { cep: digits(f.cep), rua: f.rua, numero: f.numero, complemento: f.complemento, bairro: f.bairro, cidade: f.cidade, uf: f.uf },
        frete_servico: pr?.frete?.servico, pagamento_metodo: metodo, canal: landing ? "landing" : "loja", landing, utm: getUtm(),
        ciencia_advertencias: ciencia, aceite_termos: termos, sessao,
      });
      track("purchase", { value: r.data.total, transaction_id: r.data.numero });
      setItems([]);
      nav(`/pedido/${r.data.id}/obrigado`);
    } catch (e) {
      setErrs(fieldErrors(e));
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (!items.length) return <div className="container-na py-20 text-center"><p className="text-na-ink2" data-testid="checkout-empty">Sua sacola está vazia.</p><Link to="/" className="btn-na mt-6">Ver produtos</Link></div>;
  return (
    <form onSubmit={submit} className="container-na grid gap-8 pb-16 pt-8 lg:grid-cols-[1.4fr_1fr]" noValidate data-testid="checkout-form">
      <div className="space-y-6">
        {ready && <p className="flex items-center gap-2 rounded-2xl bg-na-sage p-4 font-semibold text-na-forest" data-testid="cart-ready-notice"><CheckCircle2 className="h-5 w-5" /> Sua sacola está pronta</p>}
        <h1 className="text-4xl">Finalizar pedido</h1>
        <section className="card-na space-y-4 p-6">
          <h2 className="font-serif text-xl">1. Seus dados</h2>
          <F id="email" label="E-mail" err={errs.email}><input id="email" type="email" className="input-na" value={f.email} onChange={set("email")} onBlur={saveCart} data-testid="checkout-email" /></F>
          <F id="nome" label="Nome completo" err={errs.nome}><input id="nome" className="input-na" value={f.nome} onChange={set("nome")} data-testid="checkout-name" /></F>
          <div className="grid gap-4 sm:grid-cols-2">
            <F id="cpf" label="CPF" err={errs.cpf}><input id="cpf" inputMode="numeric" className="input-na" value={f.cpf} onChange={(e) => setF({ ...f, cpf: maskCpf(e.target.value) })} data-testid="checkout-cpf" /></F>
            <F id="celular" label="Celular" err={errs.celular}><input id="celular" inputMode="tel" className="input-na" value={f.celular} onChange={(e) => setF({ ...f, celular: maskPhone(e.target.value) })} data-testid="checkout-phone" /></F>
          </div>
        </section>
        <section className="card-na space-y-4 p-6">
          <h2 className="font-serif text-xl">2. Entrega</h2>
          <div className="grid gap-4 sm:grid-cols-3">
            <F id="cep" label="CEP" err={errs.cep}><input id="cep" inputMode="numeric" className="input-na" value={f.cep} onChange={onCep} data-testid="checkout-cep" /></F>
            <div className="sm:col-span-2"><F id="rua" label="Endereço" err={errs.rua}><input id="rua" className="input-na" value={f.rua} onChange={set("rua")} data-testid="checkout-street" /></F></div>
            <F id="numero" label="Número" err={errs.numero}><input id="numero" className="input-na" value={f.numero} onChange={set("numero")} data-testid="checkout-number" /></F>
            <F id="complemento" label="Complemento"><input id="complemento" className="input-na" value={f.complemento} onChange={set("complemento")} data-testid="checkout-complement" /></F>
            <F id="bairro" label="Bairro"><input id="bairro" className="input-na" value={f.bairro} onChange={set("bairro")} data-testid="checkout-district" /></F>
            <F id="cidade" label="Cidade"><input id="cidade" className="input-na" value={f.cidade} onChange={set("cidade")} data-testid="checkout-city" /></F>
            <F id="uf" label="UF"><input id="uf" maxLength={2} className="input-na" value={f.uf} onChange={set("uf")} data-testid="checkout-uf" /></F>
          </div>
          {pr?.fretes?.length > 0 && (
            <div className="space-y-2" role="radiogroup" aria-label="Opções de frete" data-testid="checkout-shipping-options">
              {pr.fretes.map((o) => (
                <label key={o.servico} className={`flex cursor-pointer items-center justify-between rounded-2xl border p-4 text-sm ${pr.frete?.servico === o.servico ? "border-na-forest bg-na-sage2" : "border-na-line"}`}>
                  <span className="flex items-center gap-2"><input type="radio" name="frete" checked={pr.frete?.servico === o.servico} onChange={() => setFrete(o.servico)} data-testid={`shipping-option-${o.servico}`} />{o.nome} · até {o.prazo_dias} dias úteis</span>
                  <b>{o.valor ? brl(o.valor) : "Grátis"}</b>
                </label>
              ))}
            </div>
          )}
        </section>
        <section className="card-na space-y-3 p-6">
          <h2 className="font-serif text-xl">3. Pagamento</h2>
          {[["pix", "Pix", `5% de desconto · QR Code e copia e cola`], ["cartao", "Cartão de crédito", "Em até 3x sem juros"], ["boleto", "Boleto", "Compensa em até 3 dias úteis"]].map(([k, l, d]) => (
            <label key={k} className={`flex cursor-pointer items-center gap-3 rounded-2xl border p-4 ${metodo === k ? "border-na-forest bg-na-sage2" : "border-na-line"}`}>
              <input type="radio" name="metodo" checked={metodo === k} onChange={() => { setMetodo(k); track("add_payment_info", { payment_type: k }); }} data-testid={`payment-${k}`} />
              <span><b className="text-sm">{l}</b><span className="block text-xs text-na-ink2">{d}</span></span>
            </label>
          ))}
        </section>
      </div>
      <aside className="space-y-4 lg:sticky lg:top-24 lg:self-start">
        <div className="card-na p-6">
          <h2 className="font-serif text-xl">Resumo</h2>
          <CartLines />
          <FreeShipBar pricing={pr} />
          <div className="mt-4 flex gap-2">
            <label htmlFor="cupom" className="sr-only">Cupom</label>
            <input id="cupom" className="input-na uppercase" placeholder="Cupom" value={cupom} onChange={(e) => setCupom(e.target.value)} data-testid="coupon-input" />
            <button type="button" className="btn-line shrink-0 px-5" onClick={() => setCupomOn(cupom.trim().toUpperCase() || null)} data-testid="coupon-apply-button">Aplicar</button>
          </div>
          {pr?.cupom_erro && <p className="err-na" data-testid="coupon-error">{pr.cupom_erro}</p>}
          {pr?.cupom && <p className="mt-1 text-xs font-semibold text-na-forest" data-testid="coupon-applied">Cupom {pr.cupom} aplicado</p>}
          {pr && (
            <div className="mt-4 space-y-1.5 border-t border-na-line pt-4 text-sm" data-testid="checkout-summary">
              <div className="flex justify-between"><span>Subtotal</span><span>{brl(pr.subtotal)}</span></div>
              {pr.descontos.frascos > 0 && <div className="flex justify-between text-na-acai"><span>Leve mais, pague menos</span><span>-{brl(pr.descontos.frascos)}</span></div>}
              {pr.descontos.assinatura > 0 && <div className="flex justify-between text-na-acai"><span>Assinatura</span><span>-{brl(pr.descontos.assinatura)}</span></div>}
              {pr.descontos.cupom > 0 && <div className="flex justify-between text-na-acai"><span>Cupom</span><span>-{brl(pr.descontos.cupom)}</span></div>}
              {pr.descontos.pix > 0 && <div className="flex justify-between text-na-acai"><span>Desconto Pix</span><span>-{brl(pr.descontos.pix)}</span></div>}
              <div className="flex justify-between"><span>Frete</span><span>{pr.frete ? (pr.frete.valor ? brl(pr.frete.valor) : "Grátis") : "Informe o CEP"}</span></div>
              <div className="flex justify-between pt-2 text-lg font-bold"><span>Total</span><span data-testid="checkout-total">{brl(pr.total)}</span></div>
              {metodo === "cartao" && <p className="text-right text-xs">{pr.parcelas.vezes}x de {brl(pr.parcelas.valor)} sem juros</p>}
            </div>
          )}
          {pr?.avisos?.map((a) => <p key={a} className="mt-2 text-xs text-amber-700">{a}</p>)}
          <div className="mt-4 space-y-3 text-sm">
            {pr?.restrito && (
              <label className="flex items-start gap-2 rounded-2xl bg-amber-50 p-3 text-amber-900">
                <input type="checkbox" checked={ciencia} onChange={(e) => setCiencia(e.target.checked)} className="mt-1" data-testid="checkout-warnings-checkbox" />
                <span>Li as advertências dos produtos: {pr.itens.filter((i) => i.restrito).map((i) => `${i.nome}: ${i.advertencias}`).join(" | ")}</span>
              </label>
            )}
            {errs.ciencia_advertencias && <p className="err-na">{errs.ciencia_advertencias}</p>}
            <label className="flex items-start gap-2">
              <input type="checkbox" checked={termos} onChange={(e) => setTermos(e.target.checked)} className="mt-1" data-testid="checkout-terms-checkbox" />
              <span>Li e aceito os <Link to="/paginas/termos-de-uso" className="underline" target="_blank">termos de uso</Link> e a <Link to="/paginas/privacidade" className="underline" target="_blank">política de privacidade</Link>.</span>
            </label>
            {errs.aceite_termos && <p className="err-na">{errs.aceite_termos}</p>}
          </div>
          <button className="btn-na mt-5 w-full py-4 text-base" disabled={busy} data-testid="checkout-submit-button">{busy ? "Enviando..." : "Finalizar pedido"}</button>
          <p className="mt-3 text-center text-xs text-na-ink2">Compra como convidado. Você pode criar conta depois.</p>
        </div>
      </aside>
    </form>
  );
}
